import { Injectable } from '@angular/core';
import { Observable, combineLatest, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  L1Model,
  L2Model,
  SecureLoginDataModel,
} from '../../models/crypto/crypto.models';
const WebCrypto: Crypto = window.crypto || window['msCrypto'];

@Injectable()
export class CryptoService {
  // tslint:disable-next-line:max-line-length
  private readonly publicKey =
    'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA0itppbPJeN6neMgD9wC70g8MV7lmclTZCPE/yDRlKRRFk6flv/fc+UIFB9znylBSMNnENm01Og68z8LHTsTJ9Un5T2FAzEFh9lrFufwVo+DcCT5xAlx5X+QeLkcnzXomxiQWmKv5n9BOOL6DmRNuBzb3cK+1Dx7ddOeO8sxetvsnJfHwo2nvWmtsGiECcToMtVWhnGpJr7t+00M3u8PyAYuqgBy17HMCSXM2MtyB88Ali9gV3YSDY0E5AIUrTAq2jyLISfPfhz2FYske4TqpwU2lAG0fiA23sJ4GjTuf9BVjx5oBCIpxiATi/4xsAjOzMC2yANN/9fpDNe7jHaG4SwIDAQAB';

  private sKeyL1: string;
  private sKeyL2: string;

  constructor() {
    this.initValues();
  }

  private initValues() {
    this.sKeyL1 = this.generateRandomKey();
    this.sKeyL2 = this.generateRandomKey();
  }

  public getSecureLogin(username, password, timestamp) {
    const sKeyL1$ = this.encryptRSA(this.sKeyL1);
    const sKeyL2$ = this.encryptRSA(this.sKeyL2);
    const l1Model = this.generateL1Model(username, password, timestamp);
    const l1Encrypted$ = this.encryptAES(l1Model, this.sKeyL1);

    const l2Encrypted$ = combineLatest(l1Encrypted$, sKeyL1$).pipe(
      map((values) => this.generateL2Model(username, values[0], values[1], timestamp)),
      switchMap((l2Model) => this.encryptAES(l2Model, this.sKeyL2)),
    );

    const secureLoginData$ = combineLatest(l2Encrypted$, sKeyL2$).pipe(
      map(
        (values) =>
          <SecureLoginDataModel>{
            keyToken2: values[1],
            body: {
              encryptedTokenL2: values[0],
            },
          },
      ),
    );

    return secureLoginData$;
  }

  private generateRandomKey() {
    let text = '';
    const possible = '0123456789abcdef';

    for (let i = 0; i < 16; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }

    return text;
  }

  /**
   * AES
   */
  encryptAES(model: L1Model | L2Model, randomKey: string) {
    const serializedModel = JSON.stringify(model);
    const encrypt = (key) =>
      toObservable<ArrayBuffer>(
        WebCrypto.subtle.encrypt(
          <any>{
            name: 'AES-CBC',
            iv: str2ab('RandomInitVector'),
            hash: { name: 'SHA-1' },
          },
          key,
          str2ab(serializedModel),
        ),
      );

    return this.importAESKey(randomKey).pipe(
      switchMap(encrypt),
      map((result) => btoa(ab2str(result))),
    );
  }

  private importAESKey(randomKey) {
    const publicKeyBuffer = str2ab(randomKey);
    const algorithm = { name: 'AES-CBC', hash: { name: 'SHA-1' } };
    const promise = WebCrypto.subtle.importKey('raw', publicKeyBuffer, algorithm, true, [
      'encrypt',
      'decrypt',
    ]);

    return toObservable<CryptoKey>(promise);
  }

  private encryptRSA(value) {
    const encrypt = (key) => this.generateEncrypt(value, key);

    return this.importRSAPublicKey().pipe(
      switchMap(encrypt),
      map((buffer) => btoa(ab2str(buffer))),
    );
  }

  private generateEncrypt(value: string, key: CryptoKey) {
    const valueBuffer: ArrayBuffer = str2ab(value);
    const promise = WebCrypto.subtle.encrypt(
      <any>{ name: 'RSA-OAEP', hash: { name: 'SHA-1' } },
      key,
      valueBuffer,
    );

    return toObservable<ArrayBuffer>(promise);
  }

  private importRSAPublicKey() {
    const publicKeyBuffer = str2ab(atob(this.publicKey));

    const algorithm = {
      name: 'RSA-OAEP',
      hash: { name: 'SHA-1' },
    };

    const promise = WebCrypto.subtle.importKey(
      'spki',
      publicKeyBuffer,
      algorithm,
      false,
      ['encrypt'],
    );

    return toObservable<CryptoKey>(promise);
  }

  /**
   * Generate models
   */
  private generateL1Model(username, password, timestamp): L1Model {
    return {
      grantType: 'password',
      username,
      password,
      timestamp,
      trackingInfo: {
        loginMode: 0,
        deviceId: '',
        pibankVersionCode: '',
        pibankVersionName: '',
        timeZone: '',
        versionSO: '',
      },
    };
  }

  private generateL2Model(
    username: string,
    encryptedL1: string,
    sKeyL1RSA: string,
    timestamp: string,
  ): L2Model {
    return {
      key1: sKeyL1RSA,
      username,
      token1: encryptedL1,
      timestamp,
    };
  }

  public getSecureLoginApi(body: any) {
    const sKey$ = this.encryptRSA(this.sKeyL1);
    const encrypted$ = this.encryptAES(body, this.sKeyL1);

    const secureLoginData$ = combineLatest(encrypted$, sKey$).pipe(
      map<any, any>(
        (values: Array<string>) =>
          <SecureLoginDataModel>{
            keyToken2: values[1],
            body: {
              encryptedTokenL2: values[0],
            },
          },
      ),
    );

    return secureLoginData$;
  }
}

/**
 * UTILS
 */
export function str2ab(str): ArrayBuffer {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);

  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
}

export function ab2str(buf): string {
  return String.fromCharCode.apply(null, new Uint8Array(buf));
}

export function toObservable<T>(object: Promise<T> | PromiseLike<T> | any) {
  let result$;
  const isIE = !!document['documentMode'];

  if (isIE) {
    result$ = Observable.create((observer) => {
      object.oncomplete = (event) => {
        observer.next(event.target.result);
        observer.complete();
      };

      object.onerror = (event) => {
        observer.error(event.target.result);
        observer.complete();
      };
    });
  } else {
    result$ = from(object);
  }

  return <Observable<T>>result$;
}
